import { CheckCircle2, AlertOctagon } from "lucide-react";
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { cx } from "../cx.js";
import styles from "./Toast.module.css";

interface ToastItem {
  id: number;
  text: string;
  tone: "success" | "danger";
}

const ToastContext = createContext<(text: string, tone?: ToastItem["tone"]) => void>(() => undefined);

export const useToast = () => useContext(ToastContext);

/** Kurze Rückmeldungen (höflich vorgelesen, verschwinden nach 5 s). Gefahrmeldungen bleiben länger. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);
  const show = useCallback((text: string, tone: ToastItem["tone"] = "success") => {
    const id = next.current++;
    setItems((xs) => [...xs.slice(-2), { id, text, tone }]);
    window.setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), tone === "danger" ? 9000 : 5000);
  }, []);
  const value = useMemo(() => show, [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={styles.region} role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={cx(styles.toast, styles[t.tone])}>
            {t.tone === "success" ? <CheckCircle2 aria-hidden="true" /> : <AlertOctagon aria-hidden="true" />}
            <span>{t.text}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
